import React, { useState } from 'react';
import { PROJECTS, AI_CHAT_SYSTEM_PROMPT } from '../data';
import { useIsMobile } from '../hooks/useIsMobile';

async function fetchAISummary(project) {
  const prompt = `In 2-3 sentences, write a compelling summary of this project from Mekhal's portfolio: ${project.name} — ${project.description}. Focus on impact and what it signals about Mekhal as a builder. Be specific, confident, no fluff.`;
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        system: AI_CHAT_SYSTEM_PROMPT,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    const data = await res.json();
    return data.content?.[0]?.text || project.description;
  } catch {
    return project.description;
  }
}

function StatusDot({ color }) {
  return (
    <span style={{
      display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%',
      background: color === 'green' ? '#22c55e' : color === 'amber' ? '#f59e0b' : color === 'grey' ? '#6b7280' : '#888',
      marginRight: '8px',
      boxShadow: color === 'green' ? '0 0 6px rgba(34,197,94,0.5)' : 'none',
    }} />
  );
}

function ProjectCard({ project, path }) {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleHover = async () => {
    setHovered(true);
    if (!aiSummary && !loadingAI) {
      setLoadingAI(true);
      const summary = await fetchAISummary(project);
      setAiSummary(summary);
      setLoadingAI(false);
    }
  };

  return (
    <div
      onMouseEnter={handleHover}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: '1px solid rgba(26,26,26,0.08)',
        transition: 'border-color 0.3s, box-shadow 0.3s',
        borderColor: hovered ? 'rgba(26,26,26,0.2)' : 'rgba(26,26,26,0.08)',
        boxShadow: hovered ? '0 8px 40px rgba(0,0,0,0.06)' : 'none',
        background: 'var(--bg)',
      }}
    >
      <div style={{ padding: isMobile ? '20px 16px 16px' : '32px 32px 28px', borderBottom: expanded ? '1px solid rgba(26,26,26,0.06)' : 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.2em', color: 'var(--ink-light)', textTransform: 'uppercase' }}>{project.tag}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.12em', color: 'var(--ink-light)', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
            <StatusDot color={project.statusDot} />{project.status}
          </div>
        </div>

        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.02em', marginBottom: '8px', lineHeight: 1 }}>{project.name}</h3>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontStyle: 'italic', color: 'var(--ink-mid)', marginBottom: '20px', fontWeight: 300 }}>{project.tagline}</div>

        {/* AI summary on hover — spec item K */}
        <div style={{ minHeight: '56px', transition: 'opacity 0.4s', opacity: hovered ? 1 : 0 }}>
          {loadingAI ? (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--ink-light)', letterSpacing: '0.1em' }}>
              Generating insight...
            </div>
          ) : aiSummary ? (
            <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--ink-mid)', lineHeight: 1.7, fontWeight: 300, borderLeft: '2px solid var(--accent)', paddingLeft: '14px' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '8px', letterSpacing: '0.2em', color: 'var(--accent)', display: 'block', marginBottom: '6px', textTransform: 'uppercase' }}>AI · Insight</span>
              {aiSummary}
            </div>
          ) : null}
        </div>

        {/* Metrics */}
        <div style={{ display: 'flex', gap: '0', flexWrap: 'wrap', margin: '20px 0 0', borderTop: '1px solid rgba(26,26,26,0.06)', paddingTop: '20px' }}>
          {project.metrics.map((m, i) => (
            <div key={i} style={{ flex: '1', minWidth: '80px', padding: '0 16px 0 0' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '8px', letterSpacing: '0.2em', color: 'var(--ink-light)', textTransform: 'uppercase', marginBottom: '4px' }}>{m.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 600, color: 'var(--ink)' }}>{m.value}</div>
            </div>
          ))}
        </div>

        {/* Stack */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '16px' }}>
          {project.stack.map((s, i) => (
            <span key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.1em', color: 'var(--ink-mid)', border: '1px solid rgba(26,26,26,0.12)', padding: '3px 10px' }}>{s}</span>
          ))}
        </div>

        {/* Expand button */}
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'var(--ink)', background: 'none', border: '1px solid rgba(26,26,26,0.2)',
              padding: '8px 18px', cursor: 'crosshair', transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.target.style.background = 'var(--ink)'; e.target.style.color = 'var(--bg)'; }}
            onMouseLeave={e => { e.target.style.background = 'none'; e.target.style.color = 'var(--ink)'; }}
          >
            {expanded ? '↑ Close' : (project.deck ? '↓ View Case Study' : '↓ Details')}
          </button>
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noreferrer"
              style={{
                fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em',
                textTransform: 'uppercase', color: 'var(--ink-light)',
                border: '1px solid rgba(26,26,26,0.15)', padding: '8px 18px',
                textDecoration: 'none', transition: 'all 0.2s', display: 'inline-block',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--ink)'; e.currentTarget.style.borderColor = 'var(--ink)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-light)'; e.currentTarget.style.borderColor = 'rgba(26,26,26,0.15)'; }}
            >
              ↗ Live Site
            </a>
          )}
        </div>
      </div>

      {/* Expanded case study */}
      {expanded && project.deck && (
        <div style={{ padding: '32px', background: 'var(--ink)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.3em', color: 'rgba(250,250,248,0.4)', textTransform: 'uppercase', marginBottom: '28px' }}>Case Study · {project.name}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' }}>
            {project.deck.map((slide, i) => (
              <div key={i} style={{ borderLeft: '1px solid rgba(232,197,71,0.2)', paddingLeft: '20px' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.2em', color: 'var(--accent)', textTransform: 'uppercase', marginBottom: '8px' }}>{slide.label}</div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'rgba(250,250,248,0.7)', lineHeight: 1.7, fontWeight: 300 }}>{slide.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Projects({ path }) {
  const isMobile = useIsMobile();
  return (
    // CHANGED: padding 120px → 80px top and bottom
    <section id="projects" style={{ padding: '80px 0', borderTop: '1px solid rgba(26,26,26,0.06)' }}>
      <div style={{ padding: '0 12%' }}>
        <div className="fade-in" style={{ marginBottom: '56px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.3em', color: 'var(--ink-light)', textTransform: 'uppercase', marginBottom: '16px' }}>
            {path === 'vision' ? "What I'm Building" : 'Projects & Startups'}
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 600, color: 'var(--ink)', lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '12px' }}>
            {path === 'vision' ? 'The Ventures' : 'The Work'}
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink-light)', fontWeight: 300 }}>
            Hover any card for an AI-generated insight. Click to expand the full case study.
          </p>
        </div>

        {/* CHANGED: repeat(2, 1fr) instead of auto-fit minmax — clean 2-column, no ghost cells */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px' }}>
          {PROJECTS.map(project => (
            <ProjectCard key={project.id} project={project} path={path} />
          ))}
        </div>
      </div>
    </section>
  );
}
